/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery } from 'react-query';
import { partnerSentryConfig } from './sentry';
import { formatPartnerApiError } from './errorHandler';
import {
  partnerLogin,
  partnerRegister,
  registerCompany,
  registerDiscount,
  getCompanyDiscounts,
  IPartnerUserRegistrationData,
  IPartnerLoginResponse,
  ICompanyRegistrationData,
  ICompanyResponse,
  IDiscountData,
  IDiscountResponse,
  getCompany,
  IStoreData,
} from './api';

export function useGetCompanyDiscounts() {
  return useQuery(['company-discounts'], () => getCompanyDiscounts(), {
    retry: false,
    refetchOnWindowFocus: false,
    onError: (error: any) => {
      partnerSentryConfig.captureApiError(error, {
        hook: 'useGetCompanyDiscounts',
        query: 'company-discounts',
      });
    },
  });
}

export function useGetCompany() {
  return useQuery(['company'], () => getCompany(), {
    retry: false,
    refetchOnWindowFocus: false,
    onError: (error: any) => {
      partnerSentryConfig.captureApiError(error, {
        hook: 'useGetCompany',
        query: 'company',
      });
    },
  });
}

export function useRegisterCompany({
  onSuccess,
  onError,
}: {
  onSuccess: (data: { store: IStoreData; company: ICompanyResponse }) => void;
  onError: (error: string) => void;
}) {
  return useMutation(
    ({ data, agreementId }: { data: ICompanyRegistrationData; agreementId: string }) =>
      registerCompany(data, agreementId),
    {
      onSuccess,
      onError: (error: any) => {
        partnerSentryConfig.captureApiError(error, {
          hook: 'useRegisterCompany',
          mutation: 'registerCompany',
        });
        onError(formatPartnerApiError(error));
      },
    }
  );
}

export function useRegisterDiscount({
  onSuccess,
  onError,
}: {
  onSuccess: (data: IDiscountResponse) => void;
  onError: (error: string) => void;
}) {
  return useMutation((data: IDiscountData) => registerDiscount(data), {
    onSuccess,
    onError: (error: any) => {
      partnerSentryConfig.captureApiError(error, {
        hook: 'useRegisterDiscount',
        mutation: 'registerDiscount',
      });
      onError(formatPartnerApiError(error));
    },
  });
}

export function usePartnerLogin({
  onSuccess,
  onError,
}: {
  onSuccess: (data: IPartnerLoginResponse) => void;
  onError: (error: string) => void;
}) {
  return useMutation(({ email, password }: { email: string; password: string }) => partnerLogin({ email, password }), {
    onSuccess,
    onError: (error: any) => {
      partnerSentryConfig.captureApiError(error, {
        hook: 'usePartnerLogin',
        mutation: 'partnerLogin',
      });
      onError(formatPartnerApiError(error));
    },
  });
}

export function usePartnerRegister({
  onSuccess,
  onError,
}: {
  onSuccess: (data: IPartnerLoginResponse) => void;
  onError: (error: string) => void;
}) {
  return useMutation(
    ({ data, agreementId }: { data: IPartnerUserRegistrationData; agreementId: string }) =>
      partnerRegister(data, agreementId),
    {
      onSuccess,
      onError: (error: any) => {
        partnerSentryConfig.captureApiError(error, {
          hook: 'usePartnerRegister',
          mutation: 'partnerRegister',
        });
        onError(formatPartnerApiError(error));
      },
    }
  );
}
